/**
 * Slash Commands Extension für den Document Editor.
 *
 * Ermöglicht das Einfügen von Embeds per Tastenkürzel:
 *   /scene  oder  /ansicht  — Aktuelle 3D-Ansicht als Scene einfügen
 *   /audit                  — Audit einfügen
 *   /doc                    — Paperless-Dokument einfügen
 *   /notiz                  — Annotation einfügen
 *
 * Popup wird als React-Komponente gerendert (nicht per ProseMirror-Decorations),
 * um den `this.members[i].localsInner` Fehler zu vermeiden.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorState } from '@tiptap/pm/state'

export interface SlashCommandItem {
  label: string
  aliases: string[]
  description: string
  icon: string
  action: () => void
}

export interface SlashCommandsOptions {
  commands: SlashCommandItem[]
}

/** Berechnet den aktuellen Slash-Query aus dem EditorState. */
export function getSlashQuery(state: EditorState): { query: string; from: number } | null {
  const { selection } = state
  if (!selection.empty) return null

  const $pos = selection.$from
  const textBefore = $pos.parent.textBetween(
    Math.max(0, $pos.parentOffset - 20),
    $pos.parentOffset,
    '\0',
  )

  const match = textBefore.match(/\/([a-zäöü]*)$/)
  if (!match) return null

  return {
    query: match[1].toLowerCase(),
    from: $pos.pos - match[0].length,
  }
}

export const SlashCommands = Extension.create<SlashCommandsOptions>({
  name: 'slashCommands',

  addOptions() {
    return { commands: [] }
  },

  addProseMirrorPlugins() {
    const commands = this.options.commands

    return [
      new Plugin({
        key: new PluginKey('slashCommands'),
        props: {
          handleKeyDown(view, event) {
            const slash = getSlashQuery(view.state)
            if (!slash) return false

            const filtered = commands.filter(cmd =>
              cmd.aliases.some(a => a.startsWith(slash.query)) ||
              cmd.label.toLowerCase().startsWith(slash.query)
            )

            if (event.key === 'Enter' && filtered.length > 0) {
              event.preventDefault()
              // Slash-Text löschen
              const { tr } = view.state
              tr.delete(slash.from, view.state.selection.from)
              view.dispatch(tr)
              // Ersten Treffer ausführen
              filtered[0].action()
              return true
            }

            return false
          },
        },
      }),
    ]
  },
})
